import React, { Children } from 'react';

import delve from 'dlv';
import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';
import {
  IModifierSelection,
  IPickerItem,
  IQuickConfig,
  ISanityItemOption,
  ISanityItemOptionModifier,
} from '@rbi-ctg/menu';
import { IUIRule, rulesForItem } from 'utils/menu/quick-configs';

import { QuickConfigsList } from './quick-config-list';

interface IQuickConfigItemProps {
  modifierSelections: IModifierSelection[];
  onClick: OnClick<HTMLDivElement>;
  rules: IUIRule[];
}

const isActive = ({ modifierSelections, rules = [] }: IQuickConfigItemProps): boolean => {
  return (
    !!rules.length &&
    rules.every(({ itemOption: ruleItemOpt, modifier }) => {
      const itemOption = modifierSelections.find(({ _key }) => ruleItemOpt._key === _key);
      return !!itemOption && itemOption.modifier._key === modifier._key;
    })
  );
};

const QuickConfigItem = styled.div<IQuickConfigItemProps>`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-size: 0.975rem;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: 4px;
  text-align: center;
  outline: none;

  &:focus-visible {
    outline: 2px solid ${Styles.color.tertiary};
    outline-offset: -2px;
  }

  ${props =>
    isActive(props)
      ? `
    background-color: ${Styles.color.primary};
    color: ${Styles.color.white};
    `
      : `
    color: ${Styles.color.black};
  `}
`;

type HandleModifierSelection = (
  id: string,
  modifier: ISanityItemOptionModifier,
  itemOption: ISanityItemOption
) => void;

interface IQuickConfigsProps {
  modifierSelections: IModifierSelection[];
  handleModifierSelection: HandleModifierSelection;
  pickerSelection: IPickerItem;
  quickConfigs: IQuickConfig[];
}

const onClickQuickConfig = (
  rules: IUIRule[],
  item: IPickerItem,
  handleModifierSelection: HandleModifierSelection
) => () => {
  rules.forEach(({ itemOption, modifier }) => {
    const itemOpt = item.options.find(({ _key }) => itemOption._key === _key);
    if (itemOpt) {
      return handleModifierSelection(item._id, modifier, itemOpt);
    }
  });
};

const QuickConfigs = ({
  handleModifierSelection,
  modifierSelections,
  pickerSelection = {} as IPickerItem,
  quickConfigs = [],
}: IQuickConfigsProps) => {
  if (quickConfigs.length === 0) {
    return null;
  }
  const items = quickConfigs.reduce<React.ReactNode[]>((acc, qc, i) => {
    if (!qc.rules) {
      return acc;
    }

    const rules = rulesForItem(qc.rules, pickerSelection);

    const configName: string = delve(qc, 'name.locale') || '';

    // if a rule is not present that means 1 or more
    // modifiers are not available for this item,
    // so we cannot render this quick config
    const clickHandler = onClickQuickConfig(rules, pickerSelection, handleModifierSelection);

    return rules.length !== qc.rules.length
      ? acc
      : acc.concat(
          // @ts-ignore - styled-components v5 types don't merge native HTML attributes
          <QuickConfigItem
            key={configName.concat(String(i))}
            modifierSelections={modifierSelections}
            onClick={clickHandler}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                clickHandler();
              }
            }}
            tabIndex={0}
            role="radio"
            aria-checked={isActive({ modifierSelections, onClick: clickHandler, rules })}
            aria-label={configName}
            rules={rules}
          >
            {configName}
          </QuickConfigItem>
        );
  }, []);

  if (Children.count(items) === 0) {
    return null;
  }

  return (
    // @ts-ignore - styled-components v5 types don't merge native HTML attributes
    <QuickConfigsList role="radiogroup" aria-label="Quick configuration">
      {items}
    </QuickConfigsList>
  );
};

export default QuickConfigs;
